import React from 'react';

import {
  LTActionMenu,
  LTCounterLabel,
  LTIconButton,
  LTPopover,
  LTTag,
  LTTestStatusLabel,
  LTText,
  LTTooltip,
} from '@lambdatestincprivate/lt-components';
import {
  KebabHorizontalIcon,
  ScreenFullIcon,
  ShareIcon,
} from '@primer/octicons-react';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import moment from 'moment';

import ResolutionIcon from '../../assets/resolution.svg';
import HistoryIcon from '../../assets/HistoryIcon';
import phone from '../../assets/phone.svg';

import { convertTimeTaken, timeDifference } from '../../utils/dateTimeUtils.js';
import { getStatusIconAndClassPrefix } from '../../utils/statusOptions.js';
import {
  getBrowserIcon,
  getBuildTypeImage,
  getOsIcon,
  isTestADemoTest,
} from '../../utils/utilityFunctions.js';
import { isMobileDevice } from '../../utils/testHelpers.js';
import { TestTypes } from '../../constants/test.js';

import styles from './index.module.css';

const TagsPopover = ({ tags, startIndex, totalCount }) => (
  <div className={styles.tagsPopoverContent}>
    {tags.slice(startIndex, totalCount).map((tag, index) => {
      const { name = '' } = tag || {};
      return (
        <LTTag
          variant={LTTag.Variant.SECONDARY}
          text={name}
          key={`${name}-${index}`}
        />
      );
    })}
  </div>
);

const TagList = ({ tags }) => {
  const totalTags = Array.isArray(tags) ? tags.length : 0;
  const remainingCount = totalTags - 2;

  return (
    <div className={styles.tagsRow}>
      {tags.slice(0, 2).map((tag, index) => {
        const { name = '' } = tag || {};
        return (
          <LTTag
            id={`topbar-tag-${name}`}
            text={name}
            variant={LTTag.Variant.SECONDARY}
            key={`${name}-${index}`}
          />
        );
      })}
      {remainingCount > 0 && (
        <>
          <span className={styles.visuallyHidden} id="topbar-tag-details">
            Remaining test labels
          </span>
          <LTPopover
            usePortal
            popoverConfig={{ 'aria-labelledby': 'topbar-tag-details' }}
            text={
              <TagsPopover
                tags={tags}
                startIndex={2}
                totalCount={totalTags}
              />
            }
            direction={LTPopover.Direction.TOP_RIGHT}
            extraPopoverStyles={{ zIndex: 99999 }}
          >
            <span
              role="button"
              tabIndex={0}
              aria-label={`Show ${remainingCount} more labels`}
            >
              <LTCounterLabel
                count={`+${remainingCount}`}
                scheme={LTCounterLabel.Scheme.OUTLINED}
              />
            </span>
          </LTPopover>
        </>
      )}
    </div>
  );
};

const TestDetailTopBar = ({
  testDetail,
  testVersion,
  onFullscreen,
  onHistoryClick,
  onShareClick,
  kebabMenuItems,
  breadcrumbLabel = 'Builds',
  onBreadcrumbClick,
  userInfo,
}) => {
  let {
    browser = '',
    buildName = '',
    category = '',
    createdAt,
    device = '',
    endedAt = '',
    id = '',
    name = '',
    osName = '',
    osVersion = '',
    resolution = '',
    startedAt = '',
    status = '',
    tags = [],
    type = '',
    username = '',
    version = '',
  } = testDetail || {};

  const { statusIconName = '' } = getStatusIconAndClassPrefix(status);
  const duration = convertTimeTaken(timeDifference(startedAt, endedAt, true));

  let browserIcon = getBrowserIcon(browser);
  let osIcon = getOsIcon(category);
  const mobile = isMobileDevice(testDetail);
  let buildType = type;

  if (mobile) {
    browserIcon = phone;
    version = device;
    if (buildType === TestTypes.SELENIUM) {
      buildType = 'appium';
    }
  }

  const { buildTypeImg, buildTypeName } = getBuildTypeImage(buildType);

  const organization = get(userInfo, ['data', 'organization'], {});
  const isDemoTest = isTestADemoTest(id);
  let createdAtToUse = createdAt;
  if (isDemoTest) {
    const orgIdToUse = organization?.id || '0';
    const isDemoBuildClickedTimestamp = localStorage.getItem(
      `isDemoUser-${orgIdToUse}`
    );
    if (isDemoBuildClickedTimestamp) {
      createdAtToUse = isDemoBuildClickedTimestamp;
    }
  }

  const timeSince =
    createdAtToUse &&
    new Date(createdAtToUse).getTime() / 1000 < new Date().getTime() / 1000
      ? `${moment(createdAtToUse).fromNow()}`
      : null;

  const heading = name || id;

  const displayOsVersion =
    category === 'linux' || category === 'ubuntu' ? 'Linux' : osVersion;

  return (
    <div className={styles.container} role="region" aria-label="Test details">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className={styles.breadcrumbRow}>
        <ol style={{ display: 'contents' }}>
          <li style={{ display: 'contents' }}>
            <button
              className={styles.breadcrumbLink}
              onClick={onBreadcrumbClick}
              type="button"
              aria-label={`Go to ${breadcrumbLabel}`}
            >
              {breadcrumbLabel}
            </button>
          </li>
          {buildName ? (
            <li style={{ display: 'contents' }}>
              <span className={styles.breadcrumbSeparator} aria-hidden="true">
                /
              </span>
              <LTTooltip text={buildName}>
                <LTText
                  variant={LTText.TextVariant.SUBHEADER_REGULAR}
                  color="fg.muted"
                  className={styles.breadcrumbBuildName}
                  tabIndex={0}
                  aria-label={buildName}
                >
                  {buildName}
                </LTText>
              </LTTooltip>
            </li>
          ) : null}
          <li style={{ display: 'contents' }} aria-current="page">
            <span className={styles.breadcrumbSeparator} aria-hidden="true">
              /
            </span>
            <LTTooltip text={heading}>
              <LTText
                variant={LTText.TextVariant.SUBHEADER_BOLD}
                color="fg.default"
                className={styles.breadcrumbTestName}
                tabIndex={0}
                aria-label={heading}
              >
                {heading}
              </LTText>
            </LTTooltip>
          </li>
        </ol>
      </nav>

      {/* Metadata row */}
      <div className={styles.metadataRow}>
        <div className={styles.metadataLeft}>
          {/* Status badge */}
          <LTTestStatusLabel
            iconSrc={statusIconName}
            status={status}
            width="16"
            height="16"
          />

          {/* Duration */}
          {duration ? (
            <div
              className={styles.metadataSection}
              aria-label={`Duration: ${duration}`}
            >
              <LTText
                variant={LTText.TextVariant.SMALL_REGULAR}
                color="fg.muted"
              >
                Duration
              </LTText>
              <LTText variant={LTText.TextVariant.SUBHEADER_BOLD}>
                {duration}
              </LTText>
            </div>
          ) : null}

          {/* Configurations */}
          {(version || displayOsVersion || resolution) ? (
            <div className={styles.metadataSection}>
              <LTText
                variant={LTText.TextVariant.SMALL_REGULAR}
                color="fg.muted"
              >
                Configurations
              </LTText>
              <ul
                className={styles.configList}
                aria-label="Test configurations"
                role="list"
              >
                {version ? (
                  <li className={styles.configItem}>
                    <span className={styles.configItem}>
                      <img
                        className={styles.icon}
                        src={browserIcon}
                        alt={capitalize(browser) || 'Browser'}
                      />
                      <LTText variant={LTText.TextVariant.SUBHEADER_BOLD}>
                        {version}
                      </LTText>
                    </span>
                  </li>
                ) : null}
                {version && displayOsVersion ? (
                  <li aria-hidden="true">
                    <span className={styles.dot} />
                  </li>
                ) : null}
                {displayOsVersion ? (
                  <li className={styles.configItem}>
                    <span className={styles.configItem}>
                      <img
                        className={styles.icon}
                        src={osIcon}
                        alt={capitalize(osName) || 'OS'}
                      />
                      <LTText variant={LTText.TextVariant.SUBHEADER_BOLD}>
                        {displayOsVersion}
                      </LTText>
                    </span>
                  </li>
                ) : null}
                {displayOsVersion && resolution ? (
                  <li aria-hidden="true">
                    <span className={styles.dot} />
                  </li>
                ) : null}
                {resolution ? (
                  <li className={styles.configItem}>
                    <span className={styles.configItem}>
                      <img
                        className={styles.icon}
                        src={ResolutionIcon}
                        alt="Resolution"
                      />
                      <LTText variant={LTText.TextVariant.SUBHEADER_BOLD}>
                        {resolution}
                      </LTText>
                    </span>
                  </li>
                ) : null}
                {buildTypeImg ? (
                  <>
                    <li aria-hidden="true">
                      <span className={styles.dot} />
                    </li>
                    <li className={styles.configItem}>
                      <img
                        className={styles.icon}
                        src={buildTypeImg}
                        alt={buildTypeName || 'Build type'}
                      />
                    </li>
                  </>
                ) : null}
              </ul>
            </div>
          ) : null}

          {/* Version (optional, e.g. test case version) */}
          {testVersion ? (
            <div
              className={styles.metadataSection}
              aria-label={`Version: ${testVersion}`}
            >
              <LTText
                variant={LTText.TextVariant.SMALL_REGULAR}
                color="fg.muted"
              >
                Version
              </LTText>
              <LTText variant={LTText.TextVariant.SUBHEADER_BOLD}>
                {testVersion}
              </LTText>
            </div>
          ) : null}

          {/* Executed by */}
          {timeSince && username ? (
            <div
              className={styles.metadataSection}
              aria-label={`Executed ${timeSince} by ${username}`}
            >
              <LTText
                variant={LTText.TextVariant.SMALL_REGULAR}
                color="fg.muted"
                aria-hidden="true"
              >
                {`Executed ${timeSince} by`}
              </LTText>
              <div className={styles.executedByValue}>
                <span className={styles.avatar} aria-hidden="true">
                  {(username?.[0] || '').toUpperCase()}
                </span>
                <LTText
                  variant={LTText.TextVariant.SUBHEADER_BOLD}
                  aria-hidden="true"
                >
                  {username}
                </LTText>
              </div>
            </div>
          ) : null}

          {/* Labels */}
          {tags?.length > 0 ? (
            <div className={styles.metadataSection}>
              <LTText
                variant={LTText.TextVariant.SMALL_REGULAR}
                color="fg.muted"
              >
                Labels
              </LTText>
              <TagList tags={tags} />
            </div>
          ) : null}
        </div>

        {/* Action buttons */}
        <div className={styles.actions}>
          {onFullscreen ? (
            <LTIconButton
              aria-label="Toggle fullscreen"
              icon={ScreenFullIcon}
              onClick={onFullscreen}
              size={LTIconButton.Size.SMALL}
              variant={LTIconButton.IconButtonVariants.DEFAULT}
            />
          ) : null}
          {onHistoryClick ? (
            <LTIconButton
              aria-label="View test history"
              icon={() => <HistoryIcon />}
              onClick={onHistoryClick}
              size={LTIconButton.Size.SMALL}
              variant={LTIconButton.IconButtonVariants.DEFAULT}
            />
          ) : null}
          {onShareClick ? (
            <LTIconButton
              aria-label="Share test"
              icon={ShareIcon}
              onClick={onShareClick}
              size={LTIconButton.Size.SMALL}
              variant={LTIconButton.IconButtonVariants.DEFAULT}
            />
          ) : null}
          {kebabMenuItems?.length > 0 ? (
            <LTActionMenu
              anchorType={LTActionMenu.AnchorType.ICON}
              aria-label="More options"
              iconButtonVariant={LTActionMenu.ButtonVariant.DEFAULT}
              leadingIcon={KebabHorizontalIcon}
              options={[{ list: kebabMenuItems }]}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TestDetailTopBar;
